import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines, Overline } from "./motion";
import { STATS } from "../../lib/data";
import { scrollToId } from "../../lib/scroll";
import { useLang } from "../../i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const go = () => scrollToId("#contact");
  const { c } = useLang();

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-primary)]/80 via-[var(--bg-primary)]/85 to-[var(--bg-primary)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg-primary)] via-[var(--bg-primary)]/70 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-screen-2xl mx-auto w-full px-6 md:px-12 lg:px-20 pt-28">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <Overline>{c.hero.overline}</Overline>
        </motion.div>

        <h1 className="font-serif mt-6 text-[13vw] leading-[0.86] md:text-[8.5rem] lg:text-[10rem] tracking-tighter text-[var(--text-primary)]">
          <MaskedLines lines={c.hero.h1a} />
          <span className="text-mask-line">
            <motion.span
              className="block text-[var(--accent)] italic"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              {c.hero.h1b}
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-10 flex flex-col md:flex-row md:items-end gap-8 md:gap-16 max-w-4xl"
        >
          <p className="font-sans text-base md:text-lg leading-relaxed text-[var(--text-secondary)] max-w-md">
            {c.hero.lead}
          </p>
          <button
            data-testid="hero-cta"
            onClick={go}
            className="shrink-0 group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[var(--accent)] text-[var(--bg-primary)] font-semibold hover:bg-[var(--accent-hover)] transition-colors duration-300"
          >
            {c.hero.cta}
            <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform duration-300" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="relative z-10 max-w-screen-2xl mx-auto w-full px-6 md:px-12 lg:px-20 mt-16 md:mt-24"
      >
        <div className="grid grid-cols-2 border-t border-[var(--border)] max-w-2xl">
          {c.hero.stats.map((s) => (
            <div key={s.label} className="py-6 md:py-8 border-b md:border-b-0 border-[var(--border)] md:border-r last:border-r-0 md:pr-6">
              <div className="font-serif text-4xl md:text-5xl text-[var(--text-primary)]">{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-secondary)] mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
